import { LatLngLiteral, WMSParams } from "leaflet";
import { User } from "marvin-auth-kit";

export * from "./map";

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface OutlextContext {
  settings: Settings;
  config: string;
}

export interface AppOutlextContext extends OutlextContext {
  actionsContainer: Element;
}

export interface Settings {
  title: string;
  backgroundIndicators: Indicator[];
  masks: Indicator[];
  initialPosition: MapPosition;
  locationSearchType: "nominatim" | "geopunt";
  layerProperties: LayerProperties[];
  pointInfoLinks: PointInfoLink[];
}

export interface MapPositionResponse {
  lat: number;
  long: number;
  zoom: number;
}

export interface MapPosition extends LatLngLiteral {
  zoom: number;
}

export interface Indicator extends Omit<IndicatorBase, "layers"> {
  layers: Layer[];
  groupingKey: string;
}

export interface IndicatorBase {
  id: number;
  opacity: number;
  isDefaultActive: boolean;
  isDefaultSelected: boolean;
  order: number;
  title: string;
  attribution: string;
  information: Information[];
  tags: string[];
  image: string;
  layers: LayerBase[];
}

export interface UpdateIndicator extends Omit<Indicator, "layers"> {
  layers: (Layer | PartialBy<Layer, "id">)[];
}

export interface LayersQuery {
  tags?: string[];
  indicatorId?: (number | string)[];
  searchable?: boolean;
  withLayers?: boolean;
  search?: string;
  limit: number;
  offset: number;
}

export interface LayerProperty {
  id: number;
  name: string;
  key: string;
  value: LayerPropertyValue;
}

export interface LayerBase {
  id: number;
  properties: LayerProperty[];
}

export interface Layer extends LayerBase {
  wms: LayerWMS;
  attributes: Attribute[];
  sourceType: string;
  legend: Legend;
  minZoom: number | null;
  maxZoom: number | null;
}

export type LayerFormData = PartialBy<Layer, "id">;

export type IndicatorFormData = Omit<Indicator, "id">;

export interface Information {
  label: string;
  value: string;
}

export interface LayerWMS {
  url: string;
  layers?: string;
  styles?: string;
  format: string;
  transparent: boolean;
  opacity: number;
  attribution?: string;
}

export interface ExtendedWMSParams extends WMSParams {
  tiled?: boolean;
  opacity?: number;
}

export interface Attribute {
  id: number;
  label: string;
  key: string;
  layers: string;
  search: boolean;
  grouping: boolean;
  formatting: AttributeFormatting | "";
  order: number | null;
}

export interface AttributeFormatting {
  type: "number";
  options: Intl.NumberFormatOptions;
  noDataValue: {
    value: string | number;
    min: number | "";
    max: number | "";
    label: string;
  };
}

interface Legend {
  title: string;
  type: string;
  entity: string;
  legendItems: LegendItem[];
}

interface LegendItem {
  label: string;
  color: string;
}

export interface LayerProperties {
  id: number;
  key: string;
  label: string;
  order: number;
  direction: "row" | "col";
  type: "radio" | "checkbox";
  values: LayerPropertyValue[];
}

export interface LayerPropertyValue {
  id: number;
  order: number;
  disableSettingsVariableIds: number[];
  name: string;
}

export interface SearchForm {
  search: string;
}

export interface LayerSearchFormValues {
  layer: number | "";
  attributes: string[];
  query: string;
}

export interface LocationResult {
  id: number;
  lat: number;
  lng: number;
  displayName: string;
  boundingbox: [[number, number], [number, number]];
}

export interface SelectedLocationState {
  numItems: number;
  selectedIndex: number;
  selectedItem: LocationResult | null;
}

export interface WFSGetFeatureParams {
  typeNames?: string;
  propertyName?: string;
  cql_filter?: string;
  viewParams?: string;
  count?: number;
  request?: string;
  service?: string;
  version?: string;
  outputFormat?: string;
  srsName?: string;
}

export interface Selection {
  indicators: IndicatorSelection[];
  properties: SelectionProperty[];
}

export interface IndicatorSelection {
  indicatorId: number;
  layerId: number | null;
  opacity: number;
  active: boolean;
  order: number;
}

export interface SelectionProperty {
  id: number;
  label: string;
  value: LayerPropertyValue;
  values: LayerPropertyValue[];
  order: number;
  direction: "row" | "col";
  type: "radio" | "checkbox";
}

export const PAGINATION_OPTIONS = [4, 8, 12, 16, 20];

export interface Pagination {
  offset: number;
  limit: number;
  total: number;
}

export interface TokenUser extends Omit<User, "roles"> {
  roles: string[];
}

export interface NewUser extends Omit<User, "id"> {}

export interface BasicObject {
  [key: string]: boolean | number | string | null;
}

export interface About {
  client: string;
  contact: string;
  description: string;
  projectName: string;
}

export const INVISIBLE_INDICATORS_DSA_KEY =
  "mapbase3-vito-invisible-indicator-msg-dontshowagain";

export interface PointInfoLink {
  id: number;
  label: string;
  url: string;
  projection?: string;
}
